import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOrganization, useUser } from "@clerk/react";
import { InviteMemberModal } from "./InviteMemberModal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OverflowMenuState {
  userId: string;
  x: number;
  y: number;
}

type Role = "admin" | "member";

interface DisplayMember {
  userId: string;
  email: string;
  role: Role;
  lastActiveAt: string | null;
}

interface DisplayInvite {
  id: string;
  email: string;
  role: Role;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Clerk roles come as "org:admin" / "org:member" / custom strings. Normalize
// for the UI which has only admin/member chips. Anything that isn't an admin
// role (org:admin or admin) renders as member; nothing crashes if a custom
// role string arrives, it just degrades to "member" in the chip.
function normalizeRole(raw: string | undefined | null): Role {
  if (!raw) return "member";
  const trimmed = raw.replace(/^org:/, "").toLowerCase();
  return trimmed === "admin" ? "admin" : "member";
}

// ---------------------------------------------------------------------------
// RoleChip
// ---------------------------------------------------------------------------

function RoleChip({ role }: { role: Role }) {
  return (
    <span
      className={`ac-team-row__role${role === "admin" ? " is-admin" : ""}`}
      aria-label={`Role: ${role}`}
    >
      {role === "admin" ? "Admin" : "Member"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// TeamRolesTab
// ---------------------------------------------------------------------------
//
// Reactive Clerk-only implementation. Previously this tab fetched
// /api/admin/team through the Node host (src/api/team.ts), which proxied
// Clerk's REST API. Now we read memberships + invitations directly from
// Clerk's React SDK — single source of truth, no Node round-trip, and the
// list re-renders when Clerk's local cache updates after invite/remove.
//
// Per [[feedback-spec5-pr54-64-review-patterns]] #14 (reactive snapshot
// merge): the Clerk-managed hook IS the source of truth; we don't merge
// into a local store. The only local state is pure UI (overflow menu
// position, modal open).

export function TeamRolesTab() {
  const { user } = useUser();
  const currentUserId = user?.id ?? null;

  // C69-1: previously hard-capped at pageSize: 50 with no fetchNext, so
  // orgs with > 50 members silently dropped rows AND adminCount could
  // mis-classify the "last admin" guard (e.g. 3 real admins but only 2
  // visible → demote check returned FALSE incorrectly). `infinite: true`
  // tells Clerk to keep the full list materialized; `pageSize` then just
  // controls the network batch size. 100 is Clerk's recommended page
  // size for org member lists.
  const { isLoaded, organization, memberships, invitations } = useOrganization({
    memberships: { pageSize: 100, infinite: true, keepPreviousData: true },
    invitations: { pageSize: 100, infinite: true, keepPreviousData: true, status: ["pending"] },
  });

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [overflowMenu, setOverflowMenu] = useState<OverflowMenuState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // Close overflow menu on outside click, scroll, or resize. The menu is
  // position:fixed and anchored to the trigger button's getBoundingClientRect()
  // at open time — without these listeners it detaches and floats when the
  // user scrolls the table or resizes the window.
  useEffect(() => {
    if (!overflowMenu) return;
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOverflowMenu(null);
      }
    }
    function dismiss() {
      setOverflowMenu(null);
    }
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [overflowMenu]);

  // Map Clerk's OrganizationMembershipResource[] into the UI's row shape.
  // publicUserData is undefined for SSO-suppressed users; we DROP those
  // rows because organization.updateMember/removeMember need a real user
  // id — falling back to membership.id (C69-2) made every action fail on
  // those rows AND broke the self-check (currentUserId vs membership.id).
  // Rare-but-possible operator wart: a row may be hidden if Clerk hasn't
  // yet propagated publicUserData. Refetch on tab open via revalidate.
  const members: DisplayMember[] = useMemo(() => {
    const rows = memberships?.data ?? [];
    const out: DisplayMember[] = [];
    for (const m of rows) {
      const pud = m.publicUserData;
      const userId = pud?.userId;
      if (!userId) continue;
      const email = pud?.identifier ?? userId;
      const lastActive = (pud as { lastActiveAt?: Date | string } | undefined)?.lastActiveAt;
      let lastActiveAt: string | null = null;
      if (lastActive instanceof Date) lastActiveAt = lastActive.toISOString();
      else if (typeof lastActive === "string") lastActiveAt = lastActive;
      out.push({
        userId,
        email,
        role: normalizeRole(m.role),
        lastActiveAt,
      });
    }
    return out;
  }, [memberships?.data]);

  const pendingInvites: DisplayInvite[] = useMemo(() => {
    const rows = invitations?.data ?? [];
    return rows.map((inv) => ({
      id: inv.id,
      email: inv.emailAddress,
      role: normalizeRole(inv.role),
      createdAt:
        inv.createdAt instanceof Date ? inv.createdAt.toISOString() : String(inv.createdAt),
    }));
  }, [invitations?.data]);

  const adminCount = useMemo(() => members.filter((m) => m.role === "admin").length, [members]);

  function openMenu(userId: string, e: React.MouseEvent<HTMLButtonElement>) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setOverflowMenu({ userId, x: rect.right, y: rect.bottom });
  }

  async function handleChangeRole(userId: string, newRole: Role) {
    setOverflowMenu(null);
    setActionError(null);
    if (!organization) return;
    setPendingAction(true);
    try {
      // Clerk wants the full "org:role" key. Our normalize() strips the
      // prefix for display; here we re-attach it for the API call.
      await organization.updateMember({ userId, role: `org:${newRole}` });
      // After the optimistic-cache update, refetch the list so any role
      // chip immediately reflects the new state.
      await memberships?.revalidate?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to change role.");
    } finally {
      setPendingAction(false);
    }
  }

  async function handleRemove(userId: string) {
    setOverflowMenu(null);
    setActionError(null);
    if (!organization) return;
    if (!window.confirm("Remove this member from the organization?")) return;
    setPendingAction(true);
    try {
      await organization.removeMember(userId);
      await memberships?.revalidate?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to remove member.");
    } finally {
      setPendingAction(false);
    }
  }

  function isMenuDisabled(member: DisplayMember): boolean {
    if (pendingAction) return true;
    // Disable ⋮ menu for the current user — cannot self-manage
    return member.userId === currentUserId;
  }

  function canChangeToMember(member: DisplayMember): boolean {
    if (member.role !== "admin") return false;
    return adminCount >= 2;
  }

  function canRemove(member: DisplayMember): boolean {
    if (member.userId === currentUserId) return false;
    if (member.role === "admin" && adminCount < 2) return false;
    return true;
  }

  const menuMember = overflowMenu
    ? (members.find((m) => m.userId === overflowMenu.userId) ?? null)
    : null;

  // C69-4 / follow-up: distinguish "still loading from Clerk" from "no
  // active org". Clerk returns `organization === null` (and consequently
  // `memberships === null`) when the user has no active org selected —
  // the original condition treated that as "still loading", spinning
  // forever. A later pass renamed the recoverable branch to a hard error
  // ("Failed to load team. Please try again."), but `organization === null`
  // isn't a failure here — it's the personal-workspace state documented in
  // Spec 7 (`personal:${userId}`, a real billable 1-seat workspace) and
  // AdminConsoleView deliberately routes those workspaces into this tab
  // (see the isOrgAdmin comment there). Render the honest single-user view
  // instead of a false error.
  const isPersonalWorkspace = isLoaded && organization === null;
  const loading =
    !isLoaded || (!isPersonalWorkspace && (memberships === undefined || memberships === null));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="ac-team-tab">
      <div className="ac-team-tab__header">
        <h2 className="ac-team-tab__title">Team &amp; roles</h2>
        <button
          type="button"
          className="ac-btn ac-btn--primary"
          onClick={() => setInviteModalOpen(true)}
          disabled={!organization}
          title={
            !organization
              ? "This workspace has no team yet — creating a team workspace isn't available here yet."
              : undefined
          }
        >
          ⊕ Invite member
        </button>
      </div>

      {actionError !== null && (
        <div className="ac-team-tab__action-error" role="alert">
          {actionError}
        </div>
      )}

      {loading && (
        <div className="ac-team-tab__loading" aria-live="polite">
          Loading team…
        </div>
      )}

      {!loading && isPersonalWorkspace && (
        <div className="ac-team-tab__personal" role="status">
          <p>
            <strong>Personal workspace.</strong> It&apos;s just{" "}
            {user?.primaryEmailAddress?.emailAddress ?? "you"} here — no team yet.
          </p>
          <p>
            Team workspaces (invites, shared roles) aren&apos;t available to create from this screen
            yet.
          </p>
        </div>
      )}

      {!loading && !isPersonalWorkspace && (
        <>
          {/* Members table */}
          <section className="ac-team-table" aria-label="Team members">
            <table>
              <thead>
                <tr>
                  <th scope="col">Member</th>
                  <th scope="col">Role</th>
                  <th scope="col">Last active</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  return (
                    <tr key={member.userId} className="ac-team-row">
                      <td className="ac-team-row__email">{member.email}</td>
                      <td>
                        <RoleChip role={member.role} />
                      </td>
                      <td className="ac-team-row__last-active">
                        {formatDate(member.lastActiveAt)}
                      </td>
                      <td className="ac-team-row__menu">
                        <button
                          type="button"
                          className="ac-team-row__menu-btn"
                          aria-label="More options"
                          disabled={isMenuDisabled(member)}
                          onClick={(e) => openMenu(member.userId, e)}
                        >
                          ⋮
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {/* Pending invites table */}
          {pendingInvites.length > 0 && (
            <section
              className="ac-team-table ac-team-table--invites"
              aria-label="Pending invitations"
            >
              <h3 className="ac-team-table__heading">Pending invitations</h3>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Email</th>
                    <th scope="col">Role on join</th>
                    <th scope="col">Invited</th>
                    <th scope="col">
                      <span className="sr-only">Status</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv) => (
                    <tr key={inv.id} className="ac-team-row ac-team-row--invite">
                      <td className="ac-team-row__email">{inv.email}</td>
                      <td>
                        <RoleChip role={inv.role} />
                      </td>
                      <td className="ac-team-row__last-active">{formatDate(inv.createdAt)}</td>
                      <td>
                        <span className="ac-team-row__pending-badge">Pending</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {/* Overflow menu */}
      {overflowMenu !== null && menuMember !== null && (
        <div
          ref={menuRef}
          className="ac-team-row__overflow-menu"
          role="menu"
          style={{ position: "fixed", top: overflowMenu.y, left: overflowMenu.x }}
        >
          {menuMember.role === "member" && (
            <button
              role="menuitem"
              type="button"
              onClick={() => handleChangeRole(menuMember.userId, "admin")}
            >
              Make admin
            </button>
          )}
          {menuMember.role === "admin" && canChangeToMember(menuMember) && (
            <button
              role="menuitem"
              type="button"
              onClick={() => handleChangeRole(menuMember.userId, "member")}
            >
              Make member
            </button>
          )}
          {canRemove(menuMember) && (
            <button
              role="menuitem"
              type="button"
              className="ac-team-row__menu-danger"
              onClick={() => handleRemove(menuMember.userId)}
            >
              Remove from org
            </button>
          )}
        </div>
      )}

      {/* Invite modal */}
      {inviteModalOpen && (
        <InviteMemberModal
          onClose={() => setInviteModalOpen(false)}
          onSuccess={() => {
            setInviteModalOpen(false);
            void invitations?.revalidate?.();
          }}
        />
      )}
    </div>
  );
}
